import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Stack, Paper, IconButton, Table, TableBody,
    TableContainer, TableHead, TableRow, Autocomplete
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getByIdLabAnalyzerRequest, updateLabAnalyzerTestMappingRequest, resetUpdateLabAnalyzerTestMapping, resetGetByIdLabAnalyzer } from '../../../../redux/modules/laboratory/labAnalyzer/slice';
import { getLabTestRequest } from '../../../../redux/modules/laboratory/labTest/slice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

export default function LabAnalyzerTestMapping() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = '/' + location.pathname.split('/')[1];
    const { id } = useParams();

    const { getByIdRes, updateTestMappingRes } = useSelector(state => state.labAnalyzer);
    const { getRes: testRes } = useSelector(state => state.labTest);
    const [mappings, setMappings] = useState([]);
    const [analyzerName, setAnalyzerName] = useState('');

    useEffect(() => {
        dispatch(setMainHeading("Test Mapping"));
        dispatch(getByIdLabAnalyzerRequest(id));
        dispatch(getLabTestRequest({ page: 1, limit: 500 }));
        return () => {
            dispatch(resetUpdateLabAnalyzerTestMapping());
            dispatch(resetGetByIdLabAnalyzer());
        };
    }, [dispatch, id]);

    useEffect(() => {
        if (getByIdRes?.success && getByIdRes.data) {
            setAnalyzerName(getByIdRes.data.name);
            setMappings(getByIdRes.data.testMapping?.map(m => ({
                analyzerTestCode: m.analyzerTestCode || '',
                analyzerTestName: m.analyzerTestName || '',
                lisTestId: m.lisTestId?._id || m.lisTestId || '',
                lisTestLabel: m.lisTestId?.name ? `${m.lisTestId.testCode} - ${m.lisTestId.name}` : '',
            })) || []);
        }
    }, [getByIdRes]);

    useEffect(() => {
        if (updateTestMappingRes?.success) {
            navigate(`${basePath}/laboratory/analyzers`);
        }
    }, [updateTestMappingRes, navigate, basePath]);

    const addRow = () => {
        setMappings(prev => [...prev, { analyzerTestCode: '', analyzerTestName: '', lisTestId: '', lisTestLabel: '' }]);
    };

    const removeRow = (index) => {
        setMappings(prev => prev.filter((_, i) => i !== index));
    };

    const updateRow = (index, field, value) => {
        setMappings(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
    };

    const handleSave = () => {
        const testMapping = mappings.filter(m => m.analyzerTestCode && m.lisTestId).map(m => ({
            analyzerTestCode: m.analyzerTestCode,
            analyzerTestName: m.analyzerTestName,
            lisTestId: m.lisTestId,
        }));
        dispatch(updateLabAnalyzerTestMappingRequest({ id, testMapping }));
    };

    const testOptions = testRes?.data?.map(t => ({ _id: t._id, label: `${t.testCode} - ${t.name}` })) || [];

    return (
        <Box sx={{ maxWidth: 900, mt: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Test Mapping: {analyzerName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Map analyzer test codes to LIS test definitions
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addRow}>Add Mapping</Button>
            </Stack>

            <TableContainer component={Paper} className="tableShadow">
                <Table className="commonTable">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Analyzer Test Code</StyledTableCell>
                            <StyledTableCell>Analyzer Test Name</StyledTableCell>
                            <StyledTableCell>LIS Test</StyledTableCell>
                            <StyledTableCell width={60}></StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mappings.map((row, index) => (
                            <StyledTableRow key={index}>
                                <StyledTableCell>
                                    <TextField
                                        value={row.analyzerTestCode}
                                        onChange={(e) => updateRow(index, 'analyzerTestCode', e.target.value)}
                                        size="small" fullWidth placeholder="e.g. HBA1C"
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <TextField
                                        value={row.analyzerTestName}
                                        onChange={(e) => updateRow(index, 'analyzerTestName', e.target.value)}
                                        size="small" fullWidth placeholder="e.g. HbA1c"
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Autocomplete
                                        options={testOptions}
                                        getOptionLabel={(opt) => opt.label || ''}
                                        value={testOptions.find(o => o._id === row.lisTestId) || null}
                                        onChange={(e, val) => {
                                            updateRow(index, 'lisTestId', val?._id || '');
                                            updateRow(index, 'lisTestLabel', val?.label || '');
                                        }}
                                        renderInput={(params) => <TextField {...params} size="small" placeholder="Select LIS Test" />}
                                        size="small"
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <IconButton size="small" color="error" onClick={() => removeRow(index)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                        {mappings.length === 0 && (
                            <StyledTableRow>
                                <StyledTableCell colSpan={4}>
                                    <Typography color="text.secondary" align="center">No mappings configured. Click "Add Mapping" to start.</Typography>
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
                <Button variant="outlined" onClick={() => navigate(`${basePath}/laboratory/analyzers`)}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Save Mappings</Button>
            </Stack>
        </Box>
    );
}
